import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import { Col, Card, Row, Button } from 'react-bootstrap';
import { DataTable, DataTableLoading, DataTableRecordsNotFound } from '../widgets/DataTable';
import { CardContainer } from '../widgets/CardContainer';
import { getLatestSensorList, getDeviceList as getDevices, deviceList as hardwareList } from '../../actions/hardware';
import { ButtonGroupToggle, ToggleSwitch } from '../widgets';
import { connectSocket } from '../../actions/socket';
import DeviceRowV1 from './DeviceRowV1';
import { userConfiguration, updateConfiguration, getUserConfiguration } from '../../actions/user';
import { usePermission } from '../../helpers/common';
import { NOT_APPLICABLE } from '../../constants';
import { SubNav } from '../layout/submenu';
import ScaleList from './scaleList';
import AddDeviceModal from './addDeviceModal';

const DeviceList = (props) => {
  const { match } = props;
  const dispatch = useDispatch();

  const allow = usePermission();
  const history = useHistory();
  const allowView = allow("device-management", "view");
  const allowEdit = allow("device-management", "edit");

  const [selectedViewType, setSelectedViewType] = useState(1);
  const [hardwareDeviceList, setHardwareDeviceList] = useState([]);
  const [activeTab, setActiveTab] = useState("device");
  const [isEditModal, setEditModal] = useState(false);
  const [editScaleId, setEditScaleId] = useState("");
  const [sort, setSort] = useState({
    direction: 1,
    field: null
  });
  const [showModal, setShowModal] = useState(false);

  const isRequesting = useSelector(state => state.hardware.deviceList.isRequesting);
  const deviceList = useSelector(state => state.hardware.deviceList.payload);
  const autoRefresh = useSelector(state => state.user.configuration.autoRefresh && state.user.configuration.autoRefresh.device_list ?
    state.user.configuration.autoRefresh.device_list : false);
  const isSensorRequesting = useSelector(state => state.hardware.sensorListById.isRequesting);
  const sensorsInfo = useSelector(state => state.hardware.sensorListById.payload);
  const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);

  const tabs = [
    { value: 1, label: "Active" },
    { value: 2, label: "Archived" }
  ];

  let timerId = null;
  const isDeviceRequesting = !isRequesting && !isSensorRequesting ? false : true;

  useEffect(() => {
    dispatch(getUserConfiguration("autoRefresh", { configuration_name: "AutoRefresh" }));

    if (!window.socketClient) dispatch(connectSocket());
    getDeviceList();

    return () => dispatch(hardwareList());
  }, []);

  useEffect(() => {
    if (deviceList.length === 0 || isDeviceRequesting) return;
    const devices = deviceList.filter(e => selectedViewType === 1 ? e.statusId === 1 : e.statusId === 2).map((e) => {
      let item = {
        ...e,
        operatorName: e.operatorName && String(e.operatorName).trim().length !== 0 ? e.operatorName : NOT_APPLICABLE,
        egName: e.egName && String(e.egName).trim().length !== 0 ? e.egName : NOT_APPLICABLE,
        ...e.deviceDetail,
        ...deviceSensorInfos(e.egId)
      };
      return item;
    });
    setHardwareDeviceList(devices)
  }, [isDeviceRequesting, selectedViewType, deviceList]);

  useEffect(() => {
    clearTimer();
    if (autoRefresh) timerId = setInterval(() => getDeviceList(), 5 * 60 * 1000);

    return clearTimer;
  }, [autoRefresh]);

  const clearTimer = () => {
    if (timerId) clearInterval(timerId);
    timerId = null;
  }

  const getDeviceList = () => {
    const { farmId } = match.params;
    dispatch(getDevices(farmId, (list) => {
      const egList = [];
      list.forEach(item => {
        if (item.current_hardware_info && item.current_hardware_info.equipment_group_uuid)
          egList.push({ equipment_group_uuid: item.current_hardware_info.equipment_group_uuid });
      })
      dispatch(getLatestSensorList(egList));
    }));
  };

  const handleRefreshToggle = (e) => {
    const isChecked = e.target.checked;
    const cb = (isSaved) => !isSaved && dispatch(userConfiguration("autoRefresh", { device_list: !isChecked }));

    dispatch(userConfiguration("autoRefresh", { device_list: isChecked }));
    dispatch(updateConfiguration({
      configuration_name: "AutoRefresh",
      configuration: { device_list: isChecked }
    }, cb))

    if (isChecked) handleRefresh();
  }

  const handleRefresh = () => getDeviceList();

  const handleSort = (sortField) => {
    if (!sortField) return;
    let sortedList = [];
    const { direction } = sort;
    let sortDirection = ((direction + 1) > 2) ? 0 : direction + 1;

    if (!direction) {
      const devices = deviceList.map((e) => {
        let item = {
          ...e,
          ...e.deviceDetail,
          ...deviceSensorInfos(e.egId)
        };
        return item;
      });
      setHardwareDeviceList(devices);
    }

    sortedList = hardwareDeviceList.sort((a, b) => {
      let x = null;
      let y = null;

      if (sortField === "networkLinkTime") {
        let c = a[sortField];
        let d = b[sortField];

        if (typeof c === 'object' && c.hasOwnProperty('h') && c.hasOwnProperty('m')) {
          x = c.d ? c.d : 0;
        } else x = 0;
        if (typeof d === 'object' && d.hasOwnProperty('h') && d.hasOwnProperty('m')) {
          y = d.d ? d.d : 0;
        } else y = 0;
      } else {
        x = typeof a[sortField] === "number" ? a[sortField] : String(a[sortField]).toLowerCase();
        y = typeof b[sortField] === "number" ? b[sortField] : String(b[sortField]).toLowerCase();
      }

      const desc = direction === 2 ? 1 : -1
      const asc = direction === 2 ? -1 : 1

      return (x > y) ? desc : ((y > x) ? asc : 0)
    });

    setHardwareDeviceList(sortedList);
    setSort({ direction: sortDirection, field: sortField });
  };

  const renderSortOrder = sortField => {
    const { direction, field } = sort;

    if (sortField !== field || direction === 1) return <i className="fa fa-caret-down sort-direction hidden" aria-hidden="true" />;
    if (direction === 1) return <i className="fa fa-caret-down sort-direction hidden" aria-hidden="true" />;
    if (direction === 2) return <i className="fa fa-caret-down sort-direction px-2" aria-hidden="true" />;
    if (direction === 0) return <i className="fa fa-caret-up sort-direction px-2" aria-hidden="true" />;
  }

  const deviceSensorInfos = (egId) => {
    let info = {
      battery: 0,
      canInterface: NOT_APPLICABLE,
      networkLinkTime: NOT_APPLICABLE,
      networkLinkStatus: NOT_APPLICABLE,
    }
    if (!sensorsInfo || !sensorsInfo[egId]) return info;
    const sensor = sensorsInfo[egId];
    const { percent } = sensor.battery;
    const { state } = sensor.canConnection;
    const { upTime } = sensor.qConnection;
    const { connected } = sensor.qConnection;

    return {
      ...info,
      battery: percent ? percent : 0,
      canInterface: state && [1, 2, 3].includes(state) ? state : NOT_APPLICABLE,
      networkLinkTime: upTime ? upTime : NOT_APPLICABLE,
      networkLinkStatus: connected === false ? false : connected ? connected : NOT_APPLICABLE
    }
  }
  const isDevice = activeTab === "device";
  const isScale = activeTab === "scale";
  return (
    <>
      <CardContainer lg={12} md={12} className="hardware-list device-container px-3 animate animate__fadeIn">
        <Card className={`card-list ${activeTab === "device" ? "mr-5" : ""}`}>
          <Card.Header className="hardware-header">
            <Card.Title>
              <Row className="m-0">
                {/* <Col className="p-0">
                  {" "}
                  <Heading5>Device List</Heading5>
                </Col> */}
                <Col className="d-flex header-toggle-btn">
                  <div className={`btn-name ${isDevice ? "active" : ""}`} onClick={() => setActiveTab("device")} title="Device List">Devices
                  </div>
                  <div className={`btn-name ${isScale ? "active" : ""}`} onClick={() => setActiveTab("scale")} title="Scale List">Scale Devices
                  </div>
                </Col>
                <Col className="p-0">
                  <Row className="refresh-content">
                    {isDevice && <>
                      <span className="label">Auto refresh</span>
                      <ToggleSwitch
                        className="switch mt-1"
                        title={
                          autoRefresh
                            ? "Disable auto refresh"
                            : "Enable auto refresh"
                        }
                        onChange={(e) => handleRefreshToggle(e)}
                        checked={autoRefresh}
                      />
                      <span
                        style={{ width: "20px", height: "20px" }}
                        className="d-flex"
                      >
                        {!autoRefresh && (
                          <i
                            title="Refresh"
                            className="fa fa-refresh btn-refresh mt-1"
                            aria-hidden="true"
                            onClick={handleRefresh}
                          />
                        )}
                      </span>


                      <span className={'split'}>
                        <SubNav.Divider className={'splitter'} />
                      </span>
                    </>}
                    {
                      !isDevice && <div className="add-load-btn mr-3">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => setShowModal(true)}
                          className='add-scale-device-btn'
                        >
                          <i className="fa fa-plus mr-2" />
                          Add Scale Device
                        </Button>
                      </div>
                    }

                    <span className="label mr-1">
                      <Button
                        className='btn btn-sm'
                        variant='danger'
                        onClick={() => {
                          const url = `/home/farm/${farmId}/map`;
                          history.push(url);
                        }}>
                        <i className='fa fa-times-circle mr-1' />
                        Cancel
                      </Button>
                    </span>


                  </Row>
                </Col>
              </Row>
            </Card.Title>
          </Card.Header>

          {allowView &&
            <Row className={`d-flex justify-content-end text-end py-3 mx-0 px-4 ${isDevice ? "visible" : "invisible"}`}>
              <Col className='col-auto px-0'>
                <ButtonGroupToggle
                  disabled={isDeviceRequesting}
                  size="sm"
                  options={tabs}
                  value={selectedViewType}
                  onSelect={(type) => setSelectedViewType(type)}
                  className="btn-group-toggle" />
              </Col>
            </Row>}
          <Card.Body className="overflow-auto pt-0 device_container_table">

            <Row className="m-0">
              <Col className="p-0">
                <DataTable className={`tbl-fixed ${isDevice ? "d-block" : "d-none"}`} >
                  <DataTable.Header>
                    <DataTable.Row>
                      <DataTable.Cell style={{ width: "40px" }}>
                        #
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "130px", cursor: "pointer" }} onClick={() => handleSort("deviceName")}>
                        Device Name  <span>{renderSortOrder("deviceName")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "130px", cursor: "pointer" }} onClick={() => handleSort("hardwareTypeName")}>
                        Hardware Type <span>{renderSortOrder("hardwareTypeName")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "145px", cursor: "pointer" }} onClick={() => handleSort("egName")}>
                        Installed In  <span>{renderSortOrder("egName")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "170px", cursor: "pointer" }} onClick={() => handleSort("operatorName")}>
                        Operator Name  <span>{renderSortOrder("operatorName")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "160px", cursor: "pointer" }} onClick={() => handleSort("model")}>
                        Model  <span>{renderSortOrder("model")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "160px", cursor: "pointer" }} onClick={() => handleSort("imei")}>
                        IMEI  <span>{renderSortOrder("imei")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "125px", cursor: "pointer" }} onClick={() => handleSort("appVersion")}>
                        App Version  <span>{renderSortOrder("appVersion")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "140px", cursor: "pointer" }} onClick={() => handleSort("networkName")}>
                        Network  <span>{renderSortOrder("networkName")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "125px", cursor: "pointer" }} onClick={() => handleSort("networkLinkStatus")}>
                        Network Link Status <span>{renderSortOrder("networkLinkStatus")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "140px", cursor: "pointer" }} onClick={() => handleSort("networkLinkTime")}>
                        Network Link Up/Down Time <span>{renderSortOrder("networkLinkTime")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "160px", cursor: "pointer" }} onClick={() => handleSort("canCode")}>
                        CAN Code  <span>{renderSortOrder("canCode")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "125px", cursor: "pointer" }} onClick={() => handleSort("canInterface")}>
                        CAN Interface <span>{renderSortOrder("canInterface")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "80px", cursor: "pointer" }} onClick={() => handleSort("battery")}>
                        Battery Level <span>{renderSortOrder("battery")}</span>
                      </DataTable.Cell>
                      <DataTable.Cell style={{ width: "110px" }}>
                        Actions
                      </DataTable.Cell>
                    </DataTable.Row>
                  </DataTable.Header>
                  <DataTable.Body>
                    {!isRequesting &&
                      hardwareDeviceList.length > 0 &&
                      hardwareDeviceList.map((item, i) => (
                        <DeviceRowV1
                          key={`${i}-${item.egId}`}
                          egId={item.egId}
                          index={i + 1}
                          item={item}
                          allowView={allowView}
                          allowEdit={allowEdit}
                          hardwareDeviceList={hardwareDeviceList}
                          deviceList={deviceList}
                          selectedViewType={selectedViewType}
                          farmId={match.params.farmId}
                        />
                      ))}
                  </DataTable.Body>
                </DataTable>
                <ScaleList isScale={isScale} setShow={setShowModal} setEditModal={setEditModal} setEditScaleId={setEditScaleId} />
                {isRequesting && <DataTableLoading />}
                {!isRequesting && hardwareDeviceList.length === 0 && (
                  <DataTableRecordsNotFound />
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </CardContainer>
      <AddDeviceModal
        show={showModal}
        setShow={setShowModal}
        isEdited={isEditModal}
        setEditModal={setEditModal}
        editScaleId={editScaleId}
        setEditScaleId={setEditScaleId}
      />
    </>
  );
}

export default DeviceList;
